import { useCallback, useEffect, useState } from "react";
import { ApiError, api, type CycleResponse } from "../api/client";

/**
 * Active-cycle state for the dashboard.
 *
 * <h2>Sources</h2>
 * <ul>
 *   <li>{@code GET /api/cycles/current} polled every 5s on mount —
 *       gives the dashboard refresh-resilience: after a reload the
 *       UI rediscovers the active cycle without forcing the user
 *       through Stop.</li>
 *   <li>{@code POST /api/cycles/start} and {@code /stop} — fired
 *       imperatively by the ControlPanel. The optimistic local
 *       update is overwritten by the next poll if the backend
 *       disagrees.</li>
 * </ul>
 *
 * <h2>Error mapping</h2>
 * <ul>
 *   <li>start 409 → {@link CycleStartError} with the reason string</li>
 *   <li>stop  409 NO_ACTIVE_CYCLE → {@link CycleStopError}</li>
 *   <li>Polling failures are silent (UI keeps the last observed
 *       cycle; the explicit ERROR surfaces via /api/system/state).</li>
 * </ul>
 */

export type Bias = "BUY" | "SELL";

export class CycleStartError extends Error {
  readonly reason: string;
  constructor(reason: string) {
    super(reason);
    this.reason = reason;
  }
}

export class CycleStopError extends Error {
  readonly reason: string;
  constructor(reason: string) {
    super(reason);
    this.reason = reason;
  }
}

export interface ActiveCycleState {
  cycle: CycleResponse | null;
  /** True while a start or stop request is in flight. */
  pending: boolean;
  /** True iff we've successfully observed at least one cycle this session. */
  hasObserved: boolean;
  start: (bias: Bias) => Promise<CycleResponse>;
  stop: () => Promise<CycleResponse>;
}

export function useActiveCycle(pollIntervalMs = 5_000): ActiveCycleState {
  const [cycle, setCycle] = useState<CycleResponse | null>(null);
  const [pending, setPending] = useState(false);
  const [hasObserved, setHasObserved] = useState(false);

  // Poll GET /api/cycles/current so a page reload picks up the
  // active cycle without forcing the user through Stop. Backend
  // returns 204 (parsed as null) when no cycle is active — that
  // is the legitimate "idle" state, not an error.
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const current = await api.currentCycle();
        if (cancelled) return;
        setCycle(current);
        if (current != null) setHasObserved(true);
      } catch {
        // Silent: the dashboard's system-state ERROR banner is the
        // canonical place to surface backend reachability problems.
      }
    };
    void tick();
    const id = setInterval(tick, pollIntervalMs);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [pollIntervalMs]);

  const start = useCallback(async (bias: Bias): Promise<CycleResponse> => {
    setPending(true);
    try {
      const c = await api.startCycle(bias);
      setCycle(c);
      setHasObserved(true);
      return c;
    } catch (e) {
      if (e instanceof ApiError && typeof e.body === "object" && e.body !== null) {
        const reason = (e.body as { reason?: string }).reason ?? "UNKNOWN";
        throw new CycleStartError(reason);
      }
      throw e;
    } finally {
      setPending(false);
    }
  }, []);

  const stop = useCallback(async (): Promise<CycleResponse> => {
    setPending(true);
    try {
      const c = await api.stopCycle();
      setCycle(null);
      setHasObserved(true);
      return c;
    } catch (e) {
      if (e instanceof ApiError && typeof e.body === "object" && e.body !== null) {
        const reason = (e.body as { reason?: string }).reason ?? "UNKNOWN";
        throw new CycleStopError(reason);
      }
      throw e;
    } finally {
      setPending(false);
    }
  }, []);

  return { cycle, pending, hasObserved, start, stop };
}
